import { Controller } from "@hotwired/stimulus"

// Inline bookable-hours hint for a trip leg (QPP-1474). Reads the
// admin-configured windows embedded in the page — no fetch. The server
// hard-blocks out-of-window departures on submit; this hint just tells the
// user before they get there. Vessels with no configured windows show
// nothing. windowsValue: { assetId: { wday: [[startMinute, endMinute], …] } }
export default class extends Controller {
  static targets = ["vessel", "startAt", "output"]
  static values = { windows: Object, phone: String }

  connect() {
    this.update()
  }

  update() {
    const vesselId = this.hasVesselTarget ? this.vesselTarget.value : ""
    const startRaw = this.hasStartAtTarget ? this.startAtTarget.value : ""
    const vesselWindows = this.windowsValue[vesselId]
    if (!vesselWindows || !startRaw) return this.render("")

    const start = new Date(startRaw)
    if (isNaN(start)) return this.render("")

    const dayWindows = vesselWindows[String(start.getDay())] || []
    const minute = start.getHours() * 60 + start.getMinutes()
    const inside = dayWindows.some(([s, e]) => minute >= s && minute < e)
    // A day whose windows cover all 24 hours is effectively unrestricted —
    // "bookable 00:00–24:00" is noise, so show nothing (Isaiah, 2026-08-13).
    if (inside && coversFullDay(dayWindows)) return this.render("")
    const day = `${start.toLocaleDateString("en-AU", { weekday: "long" })}s`
    const vessel = this.vesselName()
    const hours = dayWindows.map(([s, e]) => `${fmt(s)}–${fmt(e)}`).join(", ")
    const phone = this.phoneValue ? `the duty phone on ${this.phoneValue}` : "the duty phone"

    if (inside) {
      this.render(
        `<div class="alert alert-info mt-2 text-xs" role="status">` +
        `${escapeHtml(vessel)} bookable hours on ${escapeHtml(day)}: ${escapeHtml(hours)}.</div>`
      )
    } else if (dayWindows.length > 0) {
      this.render(
        `<div class="alert alert-warning mt-2 text-xs" role="alert">` +
        `<span><strong>Outside bookable hours.</strong> ${escapeHtml(vessel)} is bookable ` +
        `${escapeHtml(hours)} on ${escapeHtml(day)} — this request will be rejected. ` +
        `Choose a time within these hours, or call ${escapeHtml(phone)}.</span></div>`
      )
    } else {
      this.render(
        `<div class="alert alert-warning mt-2 text-xs" role="alert">` +
        `<span><strong>Not bookable.</strong> ${escapeHtml(vessel)} can't be booked on ` +
        `${escapeHtml(day)} — please call ${escapeHtml(phone)}.</span></div>`
      )
    }
  }

  render(html) {
    if (this.hasOutputTarget) this.outputTarget.innerHTML = html
  }

  vesselName() {
    const target = this.vesselTarget
    if (target.dataset.vesselName) return target.dataset.vesselName
    const option = target.selectedOptions && target.selectedOptions[0]
    return (option && option.text.trim()) || "This vessel"
  }
}

function coversFullDay(windows) {
  let reach = 0
  for (const [start, end] of [...windows].sort((a, b) => a[0] - b[0])) {
    if (start > reach) return false
    reach = Math.max(reach, end)
  }
  return reach >= 1440
}

function fmt(minute) {
  const h = String(Math.floor(minute / 60)).padStart(2, "0")
  const m = String(minute % 60).padStart(2, "0")
  return `${h}:${m}`
}

function escapeHtml(text) {
  const div = document.createElement("div")
  div.textContent = text
  return div.innerHTML
}
